/**
 * process.env.NODE_ENV 是 Node.js 环境变量，在 Vue 项目中由 Vue CLI 自动注入和管理，
 * 不需要你手动设置
 * 
 * 它是怎么来的？
当你用 npm run serve 或 npm run build 启动项目时，Vue CLI 会自动设置 NODE_ENV：

命令	process.env.NODE_ENV 的值
npm run serve	'development'
npm run build	'production'
npm run test:unit	'test'（如果配置了）
这些是 Vue CLI 内置的默认行为，不需要你写任何配置文件。

console.log(process.env.NODE_ENV)
执行 npm run serve，控制台会输出 development

执行 npm run build 后访问页面，控制台会输出 production
 */

// Axios 封装主文件
// src/utils/request.js
import axios from 'axios'
import { Toast } from 'vant'
import router from '@/router'
import store from '@/store'
import { SET_USER, CLEAR_USER } from '@/store/mutations-type'

// ========== 1. 环境配置 ==========
// 根据不同环境自动切换 baseURL
const baseURLMap = {
  development: 'http://localhost:8081',              // 开发环境（Vue CLI 代理）
  test: 'http://test-api.example.com',
  production: 'http://154.222.18.230:8081'
}

const request = axios.create({
  baseURL: baseURLMap[process.env.NODE_ENV] + '/api',
  timeout: 15000,                   // 默认超时 15 秒
  withCredentials: true,            // 跨域携带 Cookie（如果需要）
  headers: {
    'Content-Type': 'application/json'
  }
})

// ✅ 导出不带 /api 的图片基础地址
export const imageBaseURL = baseURLMap[process.env.NODE_ENV]

// ========== 2. 请求拦截器 ==========
request.interceptors.request.use(
  config => {
    // 2.1 跳过 Token 的请求（登录、注册等）
    if (!config._skipAuth) {
      const token = localStorage.getItem('accessToken')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }

    // 2.2 处理 FormData（自动修改 Content-Type）
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data'
    }

    // 2.3 请求日志（开发环境）
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Request] ${config.method?.toUpperCase()} ${config.url}`, config.data || config.params)
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// ========== 3. 响应拦截器 ==========
request.interceptors.response.use(
  response => {
    // 3.1 正常响应
    const { data, config } = response
    const { code, msg, data: realData } = data

    // 3.2 业务状态码统一处理（根据你的后端约定调整）
    if (code !== undefined && code !== 0 && code !== 200) {
      // 业务错误，比如 10001 表示 Token 过期
      if (code === 10001 || code === 401) {
        // Token 过期，跳转登录
        Toast('登录已过期，请重新登录')
        clearToken()

        // 避免死循环：不是登录页才跳转
        if (router.currentRoute.path !== '/login') {
          router.replace('/login')
        }
      } else {
        // 其他业务错误：如果配置了 _skipErrorHandler，由调用方自己处理
        if (!config._skipErrorHandler) {
          Toast.fail(msg || '请求失败')
        }
      }
      return Promise.reject(new Error(msg || '业务错误'))
    }

    // 3.3 成功时只返回真实数据
    return realData !== undefined ? realData : data
  },
  async error => {
    // 3.4 网络/HTTP 错误
    const { response, config } = error

    // 1. 防止重复处理
    if (config?._errorHandled) {
      return Promise.reject(error)
    }

    // 2. 如果是刷新请求本身失败，直接 reject，不再尝试刷新
    if (config?._isRefreshRequest) {
      log('刷新请求失败，不再重试');
      config._errorHandled = true;
      return Promise.reject(error);
    }

    if (response) {
      // 服务器返回了错误状态码
      const status = response.status
      if (status === 401) {
        // 未认证，尝试刷新 Token
        if (!config?._retry) {
          config._retry = true;
          log('Access Token 失效，尝试用 Refresh Token 刷新...');
          const ok = await doRefresh();
          if (ok) {
            config.headers.Authorization = 'Bearer ' + localStorage.getItem('accessToken');
            return request(config);
          }
        }
        Toast('登录已过期')
        router.replace('/login')
      } else if (status === 403) {
        Toast('没有权限访问')
      } else if (status === 404) {
        Toast('请求的资源不存在')
      } else if (status >= 500) {
        Toast('服务器异常，请稍后重试')
      } else {
        Toast(response.data?.msg || '请求失败')
      }
    } else if (error.code === 'ECONNABORTED') {
      // 超时
      Toast('请求超时，请检查网络')
    } else {
      Toast('网络异常，请稍后重试')
    }

    // 标记已处理，避免重复提示
    if (config) {
      config._errorHandled = true
    }

    return Promise.reject(error)
  }
)

// ========== 4. 文件下载/上传专用 ==========
export const downloadFile = (url, data, filename) => {
  return request({
    method: 'post',
    url,
    data,
    responseType: 'blob',      // 关键：处理二进制流
    _skipErrorHandler: true    // 让调用方自己处理错误
  }).then(res => {
    const blob = new Blob([res])
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = filename || 'download'
    link.click()
    URL.revokeObjectURL(link.href)
    return res
  }).catch(err => {
    // 错误处理：可能返回的是 JSON 格式的错误信息
    if (err.response?.data instanceof Blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const json = JSON.parse(reader.result)
            reject(new Error(json.msg || '下载失败'))
          } catch {
            reject(err)
          }
        }
        reader.readAsText(err.response.data)
      })
    }
    throw err
  })
}

// 日志打印函数
function log(msg) {
  const t = new Date().toLocaleTimeString();
  if (process.env.NODE_ENV === 'development') {
    console.log('[' + t + '] ' + msg)
  }
}

// 刷新token
async function doRefresh() {
  try {
    const data = await request.post('/auth/refresh',
      { refreshToken: localStorage.getItem('refreshToken') },
      { _skipAuth: true, _isRefreshRequest: true });

    saveToken(data);

    log('刷新成功，已获取新 Access Token');
    return true;
  } catch (e) {
    log('刷新异常，需重新登录');
    clearToken();
    return false;
  }
}

// 保存token
const saveToken = (data) => {
  localStorage.setItem('accessToken', data.accessToken)
  localStorage.setItem('refreshToken', data.refreshToken)
  localStorage.setItem('user', JSON.stringify(data.user))
  store.commit('login/' + SET_USER, data.user)
}

// 保存新的用户信息
const saveUserInfo = (data) => {
  localStorage.setItem('user', JSON.stringify(data))
  store.commit('login/' + SET_USER, data)
}

// 清除token
const clearToken = () => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
  store.commit('login/' + CLEAR_USER)
}

// ========== 5. 导出 ==========
export default request

export { doRefresh, saveToken, clearToken, saveUserInfo }